import React from "react";
import { motion } from "framer-motion";
import { useDashTheme } from "../hooks/useDashTheme";

// ── Tabs ──────────────────────────────────────────────────────────────────────
// CRM-style flat top nav: no pills — active tab is darker/bold text with an
// animated 2px underline. Sits on a surface row with a bottom border.
// tabs: [{ key, label, icon, count }]
export function Tabs({ tabs, value, onChange }) {
  const t = useDashTheme();
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 2,
        background: t.c.surface, border: `1px solid ${t.c.border}`,
        borderRadius: t.r.lg, padding: "0 8px", boxShadow: t.shadow.sm,
        overflowX: "auto", maxWidth: "100%",
      }}
    >
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            style={{
              position: "relative", display: "inline-flex", alignItems: "center", gap: 8,
              height: 46, padding: "0 14px", border: "none", background: "transparent",
              color: active ? t.c.text : t.c.textSecondary,
              fontSize: t.font.size.md, fontWeight: active ? 600 : 500,
              cursor: "pointer", letterSpacing: "-0.005em", fontFamily: t.font.family,
              whiteSpace: "nowrap", transition: "color 140ms",
            }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = t.c.text; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = t.c.textSecondary; }}
          >
            {tab.icon && (
              <span style={{ display: "inline-flex", opacity: active ? 1 : 0.7, color: active ? t.c.primary : "inherit" }}>
                {tab.icon}
              </span>
            )}
            {tab.label}
            {tab.count != null && (
              <span style={{
                fontSize: t.font.size.xs, fontWeight: 600, padding: "1px 7px", borderRadius: 99,
                background: active ? t.c.primarySoft : t.c.surfaceMuted,
                color: active ? t.c.primary : t.c.textTertiary, fontVariantNumeric: "tabular-nums",
                lineHeight: 1.5,
              }}>{tab.count}</span>
            )}
            {active && (
              <motion.span
                layoutId="dashTabUnderline"
                transition={t.motion.spring.snappy}
                style={{
                  position: "absolute", left: 10, right: 10, bottom: 0, height: 2,
                  borderRadius: 2, background: t.c.primary,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── TabPanel — fade+lift transition between panels ──────────────────────────────
export function TabPanel({ activeKey, tabKey, children }) {
  const t = useDashTheme();
  if (activeKey !== tabKey) return null;
  return (
    <motion.div
      key={tabKey}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: t.motion.ease.out }}
    >
      {children}
    </motion.div>
  );
}
